import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { getItem, setItem } from '../storage/scopedStorage'

/**
 * Interface language (T-022).
 *
 * English is the default and always will be: it is what every screen is written
 * in today, and Bangla arrives progressively. A missing Bangla key falls back
 * to English rather than showing a raw key, so a partial translation is a
 * partly-translated screen and never a broken one.
 *
 * ---------------------------------------------------------------------------
 * DOCUMENT LANGUAGE IS NOT INTERFACE LANGUAGE
 *
 * The shop needs to print a Bangla invoice from an English session and the
 * reverse. So nothing in the print path may read the ACTIVE locale — it must be
 * told which language to render in. getFixedT is exported for exactly that, and
 * it is here from the start rather than retrofitted, because retrofitting means
 * finding every place that quietly assumed the active locale.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * WHY THE DICTIONARIES ARE LOADED, NOT IMPORTED (T-044)
 *
 * They used to be two static imports, so every key of BOTH languages was in
 * main.js and every visitor downloaded the Bangla dictionary before the login
 * form could be drawn — including the ones who will never switch to it.
 *
 * That was affordable at 168 keys. It stopped being affordable the moment
 * T-023 started landing in bulk: measured, the two files were 4.08 kB gzipped
 * at 288 keys, and the remaining 417 untranslated literals project to roughly
 * 10 kB. main.js had 1.16 kB of headroom.
 *
 * So each language is a chunk of its own, fetched on demand, and only the ONE
 * in use is fetched. Adding a translation now costs nothing to anyone who is
 * not reading in that language, and main.js does not move at all.
 *
 * The load is awaited before the first render (src/index.js). Rendering first
 * and filling in after would show a frame of raw key names — "counter.tryNew"
 * where a button label belongs — which looks like a broken build.
 * ---------------------------------------------------------------------------
 *
 * NUMERALS STAY LATIN IN BOTH LANGUAGES. A confirmed decision, not an
 * oversight: no monospace face carries Bengali digits, so a column of them
 * cannot be aligned for checking, and staff would have to convert them back by
 * hand to write a challan. i18next is configured with no number formatting so
 * this cannot drift.
 */

export const LANGUAGES = ['en', 'bn']
export const DEFAULT_LANGUAGE = 'en'

const STORAGE_KEY = 'language'

export function getStoredLanguage() {
    const stored = getItem(STORAGE_KEY)
    return LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE
}

/**
 * The dictionaries, one webpack chunk each.
 *
 * A static map rather than a template literal in the import, so webpack can see
 * both names at build time and split them. `import(`../locales/${lang}.json`)`
 * would make it bundle every JSON file in that directory into one chunk, which
 * is the thing being avoided.
 */
const DICTIONARIES = {
    en: () => import(/* webpackChunkName: "locale-en" */ '../locales/en.json'),
    bn: () => import(/* webpackChunkName: "locale-bn" */ '../locales/bn.json'),
}

const loaded = new Set()

/**
 * Fetches a language's dictionary and hands it to i18next. Idempotent, and safe
 * to call for a language that is already in memory.
 *
 * @param {string} language
 * @returns {Promise<void>}
 */
export async function loadLanguage(language) {
    const lang = LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE
    if (loaded.has(lang)) return

    const module = await DICTIONARIES[lang]()
    // `.default` under webpack's ESM interop, the object itself under Jest's
    // CommonJS transform. Both shapes appear, depending on who is running.
    i18n.addResourceBundle(lang, 'translation', module.default || module, true, true)
    loaded.add(lang)
}

/**
 * Switch the interface language.
 *
 * Returns a promise, because the dictionary may still be a network fetch away
 * (T-044) — but everything that can be settled synchronously is settled before
 * that promise is created. The stored preference and <html lang> take effect on
 * this tick, so a caller that does not await still gets correct behaviour from
 * everything except i18next's own `language`, which cannot change until the
 * words exist.
 *
 * Callers in the UI do not await it: react-i18next re-renders on
 * `languageChanged`, so the screen updates itself when the chunk lands.
 */
export function setLanguage(language) {
    const next = LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE

    // Scoped per shop and user (T-020). Before login the scope resolves to a
    // single consistent anonymous bucket, so the login screen has a language
    // too without needing a special case.
    setItem(STORAGE_KEY, next)
    // Screen readers and the browser both use this to pick a voice and a font,
    // and :lang(bn) in CSS depends on it for the Bengali line-height (T-024).
    // Synchronous on purpose — see above.
    if (typeof document !== 'undefined') document.documentElement.lang = next

    // Fetch before switching. Changing the language first would render it
    // against an empty dictionary — every string falling back to English, or to
    // a raw key where English has not been loaded either.
    return loadLanguage(next).then(() => i18n.changeLanguage(next)).then(() => next)
}

i18n
    .use(initReactI18next)
    .init({
        // Empty at init; filled by loadLanguage() before the first render.
        resources: {},
        lng: getStoredLanguage(),
        fallbackLng: DEFAULT_LANGUAGE,
        // A missing key shows the English text, not the key itself. Staff
        // should never see "sales.order.title" on a screen.
        returnEmptyString: false,
        interpolation: {
            // React escapes for us; i18next doing it again double-escapes any
            // apostrophe in a shop or customer name.
            escapeValue: false,
        },
        // Silent in tests and production; noisy only when developing.
        debug: false,
    })

/**
 * Everything needed before the first paint: the stored language, and English
 * behind it so a key missing from a partial translation falls back to real text
 * rather than to its own name.
 *
 * Awaited in src/index.js. Both loads run together — the fallback is not a
 * second round trip.
 */
export function initI18n() {
    const active = getStoredLanguage()
    return Promise.all([
        loadLanguage(active),
        active === DEFAULT_LANGUAGE ? Promise.resolve() : loadLanguage(DEFAULT_LANGUAGE),
    ])
}

if (typeof document !== 'undefined') {
    document.documentElement.lang = getStoredLanguage()
}

/**
 * A translator fixed to one language, whatever the interface is set to.
 *
 * The invoice print path uses this. Nothing there may call the ordinary
 * useTranslation, or printing a Bangla invoice would silently follow whatever
 * the salesperson happened to have the interface set to.
 */
export function getFixedT(language) {
    return i18n.getFixedT(LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE)
}

export default i18n
